import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Send, Download, RefreshCw } from 'lucide-react';
import toolkitService from '../services/ToolkitService';
import Button from './shared/Button';
import { useToolkitProject, ToolkitProjectHeader } from './toolkit/ToolkitProject';
import { writeWorkbook, downloadBlob } from '../utils/xlsx';
import { columnsOf, labelOf } from '../utils/syncConnectResult';
import useOnPrem, { syncConnectStatus } from './toolkit/useOnPrem';
import './OhipTransactionsPage.css';
import './ToolkitBlocksPage.css';

// Sync › project › Toolkit › On-premise › <query> — the desktop's SQL-script screens (Profile Notes, Reservation
// Comments, AR Accounts, Reservations, Blocks, Stay History, UDFs) for one project.
//
// The property's OPERA v5 database is only reachable from inside its network, so the question goes to the project's
// SyncConnect and the answer comes back when it next checks in (toolkit onprem.js, over the ad-hoc round trip). The
// screen sends the request, shows it as queued → delivered → returned, refreshes on its own while it waits, and keeps
// the earlier answers for this query so a result can be looked at again without asking twice.
//
// The query is one of a fixed catalogue on the server; this screen only names it. Each request is audited as a READ.
// With no SyncConnect on the project, the user runs the script themselves and uploads the result (ToolkitManualRun).

const PENDING = new Set(['QUEUED', 'DELIVERED']);
const REFRESH_MS = 5000;
const GIVE_UP_MS = 15 * 60 * 1000;
const when = (v) => (v ? new Date(v).toLocaleString() : '—');
const errText = (e) => e?.response?.data?.message || e?.response?.data?.error?.message || e.message;

function ToolkitOnPremPage() {
    const { key } = useParams();
    const toolkit = useToolkitProject();
    const { ctx, ready } = toolkit;
    const onPrem = useOnPrem(ctx);
    const [info, setInfo] = useState(null);
    const [history, setHistory] = useState([]);
    const [selected, setSelected] = useState(null);
    const [detail, setDetail] = useState(null);
    const [msg, setMsg] = useState('');
    const [busy, setBusy] = useState(false);
    const [waitSince, setWaitSince] = useState(null);
    const [search, setSearch] = useState('');
    // The query's own parameters (dates), when it takes any — checked again on the server.
    const [params, setParams] = useState({});
    const paramNames = Object.keys(info?.params || {});
    const paramsReady = paramNames.every((n) => /^\d{4}-\d{2}-\d{2}$/.test(params[n] || ''));

    const loadHistory = useCallback(async () => {
        const { data } = await toolkitService.onprem(ctx, 'list', { key });
        setHistory(data.items || []);
        return data.items || [];
    }, [ctx, key]);

    useEffect(() => {
        if (!ready) return;
        toolkitService.onprem(ctx, 'catalog').then(({ data }) => setInfo((data.items || []).find((q) => q.key === key) || null)).catch(() => {});
        loadHistory().then((items) => { if (items[0]) setSelected(items[0].id); }).catch((e) => setMsg(errText(e)));
    }, [ready, ctx, key, loadHistory]);

    // The selected request's detail (and its rows, once returned).
    useEffect(() => {
        if (!selected) { setDetail(null); return; }
        toolkitService.onprem(ctx, 'get', { id: selected }).then(({ data }) => setDetail(data)).catch((e) => setMsg(errText(e)));
    }, [selected, ctx]);

    // While the selected request is still on its way, look again every few seconds — until it returns, or a while has
    // passed with no answer (the SyncConnect client may be offline; the request stays queued and can be checked later).
    useEffect(() => {
        if (!detail || !PENDING.has(detail.status)) return undefined;
        if (waitSince && Date.now() - waitSince > GIVE_UP_MS) return undefined;
        const t = setTimeout(async () => {
            try {
                const { data } = await toolkitService.onprem(ctx, 'get', { id: detail.id });
                setDetail(data);
                if (!PENDING.has(data.status)) loadHistory().catch(() => {});
            } catch (e) { setMsg(errText(e)); }
        }, REFRESH_MS);
        return () => clearTimeout(t);
    }, [detail, ctx, loadHistory, waitSince]);

    const ask = async () => {
        setBusy(true);
        setMsg('');
        try {
            // No SyncConnect: the "run it yourself" dialog (useOnPrem) — it resolves once the result is uploaded.
            const { syncConnect } = await syncConnectStatus(ctx);
            const { data } = syncConnect
                ? await toolkitService.onprem(ctx, 'submit', { key, params })
                : { data: await onPrem.ask(key, params) };
            setWaitSince(Date.now());
            await loadHistory();
            setSelected(data.id);
        } catch (e) {
            setMsg(errText(e));
        } finally {
            setBusy(false);
        }
    };

    const result = detail?.result;
    const cols = useMemo(() => (result ? columnsOf(result) : []), [result]);
    const rows = useMemo(() => {
        const all = result?.rows || [];
        const q = search.trim().toLowerCase();
        return q ? all.filter((r) => cols.some((c) => String(r[c] ?? '').toLowerCase().includes(q))) : all;
    }, [result, cols, search]);

    const exportXlsx = async () => {
        downloadBlob(await writeWorkbook([{ name: info?.title || key, columns: cols.map((c) => labelOf(result, c)),
            rows: rows.map((r) => cols.map((c) => r[c] ?? '')) }]), `${key}-${new Date().toISOString().slice(0, 10)}.xlsx`);
    };

    const pendingTooLong = detail && PENDING.has(detail.status) && waitSince && Date.now() - waitSince > GIVE_UP_MS;

    return (
        <div className="otx-page">
            <ToolkitProjectHeader toolkit={toolkit} title={info?.title || 'On-premise query'}
                subtitle={`${info?.description || ''} Read from the property's OPERA v5 database through the project's SyncConnect.`} />

            <div className="tkb-step">
                {info && !info.verified && (
                    <p className="tkb-hint" style={{ marginTop: 0 }}>
                        This query is the desktop app's, and reads OXI / interface tables the platform's v5 data dictionary does not
                        cover — it has not been checked against the schema here. If OPERA rejects it, the error shows below.
                    </p>
                )}
                <div className="tkb-row">
                    {paramNames.map((n) => (
                        <label key={n}>
                            <span>{n === 'startDate' ? 'Departed from' : n === 'endDate' ? 'Departed to' : n}</span>
                            <input type="date" value={params[n] || ''} onChange={(e) => setParams((p) => ({ ...p, [n]: e.target.value }))} />
                        </label>
                    ))}
                    <Button variant="success" onClick={ask} disabled={!ready || busy || !paramsReady || (detail && PENDING.has(detail.status))}>
                        <Send className="icon" /> Ask SyncConnect
                    </Button>
                    {history.length > 0 && (
                        <label>
                            <span>Answer</span>
                            <select value={selected || ''} onChange={(e) => setSelected(Number(e.target.value))}>
                                {history.map((h) => (
                                    <option key={h.id} value={h.id}>{when(h.created_at)} — {h.status}{h.row_count != null ? ` (${h.row_count} rows)` : ''}</option>
                                ))}
                            </select>
                        </label>
                    )}
                    {detail && PENDING.has(detail.status) && (
                        <Button variant="secondary" onClick={() => { setWaitSince(Date.now()); setDetail({ ...detail }); }}><RefreshCw className="icon" /> Check now</Button>
                    )}
                </div>
                {msg && <p className="tkb-msg tkb-fail">{msg}</p>}
                {detail && (
                    <p className="tkb-msg">
                        <span className={`tkb-badge tkb-status-${detail.status === 'RETURNED' ? 'success' : detail.status === 'ERROR' ? 'failed' : 'processing'}`}>{detail.status}</span>
                        {' '}asked {when(detail.created_at)}
                        {detail.delivered_at ? ` · picked up ${when(detail.delivered_at)}` : ''}
                        {detail.returned_at ? ` · answered ${when(detail.returned_at)}` : ''}
                        {detail.query_duration ? ` · ran ${detail.query_duration}` : ''}
                        {detail.status === 'QUEUED' && ' — waiting for the property\'s SyncConnect to check in.'}
                        {detail.status === 'AWAITING_UPLOAD' && ' — the script was given out to run by hand and no result was uploaded. Ask again for a new script.'}
                        {detail.delivery === 'UPLOAD' && detail.status === 'RETURNED' && ' — run by hand; the result is the file that was uploaded.'}
                    </p>
                )}
                {pendingTooLong && <p className="tkb-hint">No answer yet — the SyncConnect client may be offline. The request stays queued; come back and pick it from the list.</p>}
                {detail?.status === 'ERROR' && <p className="tkb-msg tkb-fail">{detail.error_message}</p>}
                {detail?.result_truncated === 'Y' && <p className="tkb-hint">The answer was too large to keep whole — only the first {result?.rows?.length} rows are shown.</p>}
            </div>

            {result && (
                <>
                    <div className="tkb-toolbar">
                        <input className="tkb-search" type="search" placeholder="Search…" value={search} onChange={(e) => setSearch(e.target.value)} />
                        <span className="tkb-hint">{rows.length} of {result.rows?.length || 0} row(s)</span>
                        <div className="tkb-toolbar-spacer" />
                        <Button variant="secondary" onClick={exportXlsx} disabled={!rows.length}><Download className="icon" /> Export</Button>
                    </div>
                    <div className="otx-table-wrap">
                        <table className="otx-table">
                            <thead><tr>{cols.map((c) => <th key={c}>{labelOf(result, c)}</th>)}</tr></thead>
                            <tbody>
                                {rows.slice(0, 2000).map((r, i) => <tr key={i}>{cols.map((c) => <td key={c}>{r[c] ?? ''}</td>)}</tr>)}
                                {!rows.length && <tr><td colSpan={Math.max(cols.length, 1)} className="otx-empty">No rows.</td></tr>}
                            </tbody>
                        </table>
                    </div>
                    {rows.length > 2000 && <p className="tkb-hint">Showing the first 2,000 rows — Export has all {rows.length}.</p>}
                </>
            )}
        </div>
    );
}

export default ToolkitOnPremPage;
